import React, { useContext } from 'react';
import { Box, Text, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: .55; }
`;

// One placeholder mark. Everything below is built from these, drawn in the shape of what will
// replace them, so the card reads the same before and after its data arrives.
export const SkeletonBlock = styled(Box).attrs((props) => ({
    background: 'chart-muted',
    round: props.round || '4px',
    flex: false,
}))`
    animation: ${pulse} 1.6s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// A line of text of the given size, centred in the height the text would take
export const SkeletonText = ({ width, height = '12px', lineHeight = '24px' }) => (
    <Box height={lineHeight} justify="center"><SkeletonBlock width={width} height={height} /></Box>
);

const SALES_BARS = [62, 74, 54, 82, 92, 80, 66, 88, 58, 76, 84, 60];
const PAID_BARS = [52, 70, 58, 66, 78, 86, 72, 62, 70, 90, 66, 64];

const Axis = ({ count, justify = 'around' }) => (
    <Box direction="row" justify={justify} height="24px" align="start" flex={false}>
        {Array.from({ length: count }, (_, index) => <SkeletonBlock key={index} width="28px" height="12px" />)}
    </Box>
);

// The sales and payments bars
export const ChartBarsSkeleton = ({ height = 190, months = 6 }) => (
    <Box height={`${height}px`} gap="xsmall" flex={false}>
        <Box flex direction="row" justify="around" align="end">
            {Array.from({ length: months }, (_, index) => (
                <Box key={index} direction="row" gap="2px" align="end" fill="vertical">
                    <SkeletonBlock width="12px" height={`${SALES_BARS[index]}%`} round={{ corner: 'top', size: '4px' }} />
                    <SkeletonBlock width="12px" height={`${PAID_BARS[index]}%`} round={{ corner: 'top', size: '4px' }} />
                </Box>
            ))}
        </Box>
        <Axis count={months} />
    </Box>
);

const WEEK_BARS = [58, 72, 46, 64, 80, 52, 68, 40, 76, 60, 50, 70, 44];

// A year of weekly bars
export const ChartWeeksSkeleton = ({ height = 190 }) => (
    <Box height={`${height}px`} gap="xsmall" flex={false}>
        <Box flex direction="row" justify="between" align="end">
            {Array.from({ length: 52 }, (_, index) => (
                <SkeletonBlock key={index} width="min(8px, 1.3%)" height={`${WEEK_BARS[index % WEEK_BARS.length]}%`} round={{ corner: 'top', size: '3px' }} />
            ))}
        </Box>
        <Axis count={4} />
    </Box>
);

// The payment period line
export const ChartLineSkeleton = ({ height = 190 }) => {
    const theme = useContext(ThemeContext);
    const color = normalizeColor('chart-muted', theme);
    return (
        <Box height={`${height}px`} gap="xsmall" flex={false}>
            <Box flex pad={{ horizontal: '16px' }}>
                <svg width="100%" height="100%" viewBox="0 0 400 150" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M0 78 C 60 70, 110 66, 170 64 S 250 58, 290 42 S 370 40, 400 46" fill="none" stroke={color} strokeWidth="3" vectorEffect="non-scaling-stroke" />
                    <path d="M0 96 H 400" fill="none" stroke={color} strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
                </svg>
            </Box>
            <Axis count={6} justify="between" />
        </Box>
    );
};

const NAME_WIDTHS = ['52%', '40%', '46%', '36%', '44%'];
const TRACK_WIDTHS = ['100%', '78%', '52%', '40%', '30%'];

// A heading and `rows` rows (three unless the card shows more) laid out like ChartRow. `title` is real text when it never changes;
// without one the heading itself is waiting on data. `tab` leaves room for the Sales/Cash tabs.
export const ChartRowsSkeleton = ({ title, summary, tab, track, noHeading, oneValue, rows = 3 }) => (
    <Box gap="small">
        {!noHeading && <Box direction="row" justify="between" align="center" gap="small">
            <Box>
                {title ? <Text weight={500}>{title}</Text> : <SkeletonText width="220px" height="14px" />}
                {summary && <SkeletonText width="140px" lineHeight="20px" />}
            </Box>
            {tab && <SkeletonBlock width="114px" height="36px" />}
        </Box>}
        <Box gap={track ? 'xsmall' : undefined}>
            {NAME_WIDTHS.slice(0, rows).map((width, index) => (
                <Box key={width} gap="xxsmall" pad={{ vertical: track ? 'xsmall' : 'xxsmall', horizontal: 'xxsmall' }}>
                    <Box direction="row" justify="between" align="center" gap="small">
                        <Box flex><SkeletonText width={width} /></Box>
                        <Box direction="row" gap="small" align="center" flex={false}>
                            <SkeletonText width="64px" />
                            {!oneValue && <Box width="48px" align="end"><SkeletonText width="36px" /></Box>}
                        </Box>
                    </Box>
                    {track && <SkeletonBlock width={TRACK_WIDTHS[index]} height="10px" />}
                </Box>
            ))}
        </Box>
    </Box>
);
